// src/components/ProductsManagement.js
import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, X, Upload, Check, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';

const ProductsManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortField, setSortField] = useState('displayOrder');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [currentProduct, setCurrentProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    displayOrder: '',
    active: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [notification, setNotification] = useState(null);

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/admin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setProducts(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products. Please try again.');
      setLoading(false);
    }
  };

  // Handle form inputs 
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle image input
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle updating display order
  const handleUpdateDisplayOrder = async (productId, newDisplayOrder) => {
    try {
      if (newDisplayOrder < 1) {
        newDisplayOrder = 1; // Minimum order is 1
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/admin/${productId}/display-order`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ displayOrder: newDisplayOrder })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Refresh products after successful update
      await fetchProducts();
      showNotification('Display order updated successfully!');
      
    } catch (error) {
      console.error('Error updating display order:', error);
      showNotification('Failed to update display order. Please try again.', 'error');
    }
  };

  // Move product up in display order (decrease the number)
  const moveProductUp = (product) => {
    const currentOrder = product.displayOrder || 999;
    handleUpdateDisplayOrder(product._id, currentOrder - 1);
  };

  // Move product down in display order (increase the number)
  const moveProductDown = (product) => {
    const currentOrder = product.displayOrder || 999;
    handleUpdateDisplayOrder(product._id, currentOrder + 1);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      displayOrder: '',
      active: true
    });
    setImageFile(null);
    setImagePreview('');
  };

  // Unique list of categories currently in use, for filter + datalist suggestions
  const existingCategories = Array.from(
    new Set(products.map(p => p.category).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  // Open modal for adding new product
  const openAddModal = () => {
    resetForm();
    setModalMode('add');
    setIsModalOpen(true);
  };

  // Open modal for editing product
  const openEditModal = (product) => {
    setCurrentProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      displayOrder: product.displayOrder || '',
      active: product.active
    });
    // Handle both Cloudinary URLs and local paths
    setImagePreview(product.image.startsWith('http') ? product.image : process.env.REACT_APP_BACKEND_URL + product.image);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentProduct(null);
    resetForm();
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.price || !formData.category) {
      showNotification('All fields are required', 'error');
      return;
    }

    if (modalMode === 'add' && !imageFile) {
      showNotification('Image is required', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = modalMode === 'add' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/products/admin`
        : `${process.env.REACT_APP_BACKEND_URL}/api/products/admin/${currentProduct._id}`;
      
      const method = modalMode === 'add' ? 'POST' : 'PUT';
      
      // FormData for sending files
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('category', formData.category);
      
      if (formData.displayOrder) {
        formDataToSend.append('displayOrder', formData.displayOrder);
      }
      
      if (modalMode === 'edit') {
        formDataToSend.append('active', formData.active);
      }
      
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }
      
      const response = await fetch(apiUrl, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Refresh products
      await fetchProducts();
      
      // Close modal and show success notification
      closeModal();
      showNotification(
        modalMode === 'add' ? 'Product added successfully!' : 'Product updated successfully!'
      );
      
    } catch (error) {
      console.error('Error saving product:', error);
      showNotification('Failed to save product. Please try again.', 'error');
    }
  };

  // Handle delete product
  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/admin/${productId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove from local state
      setProducts(products.filter(product => product._id !== productId));
      showNotification('Product deleted successfully!');
      
    } catch (error) {
      console.error('Error deleting product:', error);
      showNotification('Failed to delete product. Please try again.', 'error');
    }
  };

  // Change sort field
  const handleSortChange = (field) => {
    setSortField(field);
  };

  // Filter and sort products
  const filteredAndSortedProducts = products
    .filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            product.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sortField === 'displayOrder') {
        const orderA = a.displayOrder || 999;
        const orderB = b.displayOrder || 999;
        return orderA - orderB;
      } else if (sortField === 'price') {
        return a.price - b.price;
      } else if (sortField === 'name') {
        return a.name.localeCompare(b.name);
      } else {
        // Default to newest first
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

  return (
    <div className="bg-amber-50 rounded-lg shadow p-6">
      {/* Notification */}
      {notification && (
        <div 
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-2 ${
            notification.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {notification.type === 'success' ? (
            <Check className="w-5 h-5" />
          ) : (
            <AlertTriangle className="w-5 h-5" />
          )}
          <p>{notification.message}</p>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-rose-800">Products Management</h2>
        <div className="flex gap-4 flex-wrap">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
          >
            <option value="all">All Categories</option>
            {existingCategories.map(cat => (
              <option key={cat} value={cat} className="capitalize">{cat}</option>
            ))}
          </select>
          <select
            value={sortField}
            onChange={(e) => handleSortChange(e.target.value)}
            className="px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
          >
            <option value="displayOrder">Sort by Display Order</option>
            <option value="name">Sort by Name</option>
            <option value="price">Sort by Price</option>
            <option value="createdAt">Sort by Date Added</option>
          </select>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-rose-800 text-white rounded-md hover:bg-rose-700"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading products...</div>
      ) : error ? (
        <div className="text-center py-8 text-red-600">{error}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSortedProducts.length > 0 ? (
            filteredAndSortedProducts.map(product => (
              <div 
                key={product._id}
                className={`bg-white rounded-lg shadow overflow-hidden ${!product.active ? 'opacity-60' : ''}`}
              >
                <div className="h-48 overflow-hidden relative">
                  <img 
                    src={product.image.startsWith('http') ? product.image : process.env.REACT_APP_BACKEND_URL + product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/400/320';
                      e.target.alt = 'Image not available';
                    }}
                  />
                  {!product.active && (
                    <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
                      <span className="bg-rose-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        Inactive
                      </span>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-white p-1 rounded-md shadow-md">
                    <span className="text-xs font-medium">
                      Order: {product.displayOrder || 999}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{product.name}</h3>
                      <p className="text-sm text-gray-600 capitalize">{product.category}</p>
                    </div>
                    <span className="font-bold text-rose-800">${product.price}</span>
                  </div>
                  
                  <div className="flex items-center mt-3 mb-2 justify-between">
                    <span className="text-sm text-gray-600">Display Order:</span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => moveProductUp(product)}
                        className="p-1 bg-gray-100 hover:bg-gray-200 rounded"
                        title="Move earlier in order"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <span className="font-medium">{product.displayOrder || 999}</span>
                      <button
                        onClick={() => moveProductDown(product)}
                        className="p-1 bg-gray-100 hover:bg-gray-200 rounded"
                        title="Move later in order"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-sm mt-2 text-gray-700 line-clamp-2">{product.description}</p>
                  <div className="mt-4 flex justify-between">
                    <button
                      onClick={() => openEditModal(product)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 flex items-center gap-1"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(product._id)}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              No products found matching your search
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b p-4">
              <h3 className="text-xl font-bold text-rose-800">
                {modalMode === 'add' ? 'Add New Product' : 'Edit Product'}
              </h3>
              <button 
                onClick={closeModal}
                className="p-2 hover:bg-rose-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price ($)
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    name="category"
                    list="category-options"
                    value={formData.category}
                    onChange={handleInputChange}
                    placeholder="e.g., Cake Collection"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                    required
                  />
                  <datalist id="category-options">
                    {existingCategories.map(cat => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                  <p className="text-xs text-gray-500 mt-1">Pick an existing one or type a new name</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="Lower numbers show first"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                </div>
              </div>

              {modalMode === 'edit' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500"
                  />
                  <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                    Active (visible to customers)
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Image
                </label>
                <div className="mt-1 flex items-center space-x-4">
                  <div className="w-24 h-24 border rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                    {imagePreview ? (
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-400 text-sm text-center">
                        No image selected
                      </span>
                    )}
                  </div>
                  <div>
                    <label className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded cursor-pointer flex items-center gap-1">
                      <Upload className="w-4 h-4" />
                      <span>
                        {modalMode === 'add' || !imagePreview ? 'Upload Image' : 'Change Image'}
                      </span>
                      <input
                        type="file"
                        onChange={handleImageChange}
                        accept="image/*"
                        className="hidden"
                        required={modalMode === 'add'}
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Recommended size: 800x800px. Max size: 5MB
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t mt-6 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-rose-800 text-white rounded-md hover:bg-rose-700"
                >
                  {modalMode === 'add' ? 'Add Product' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsManagement;